import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AuthProvider as OIDCAuthProvider, useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";

import { getEnvConfig } from "@app/utils";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import type { AuthContextValue, AuthProviderType } from "@honeycomb/mystique";
import { AuthContext } from "@honeycomb/mystique";
import { WebStorageStateStore } from "oidc-client-ts";

const AUTH_FLOW_KEY = "honeycomb_auth_flow";

const Auth0Adapter = ({ children }: { children: ReactNode }) => {
  const { getAccessTokenSilently, loginWithRedirect, logout, isLoading, isAuthenticated } = useAuth0();

  const value: AuthContextValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      provider: "auth0" as const,
      availableProviders: ["auth0"] as AuthProviderType[],
      getAccessToken: () => getAccessTokenSilently(),
      login: (opts) =>
        loginWithRedirect({
          authorizationParams: {
            connection: opts?.connection,
          },
        }),
      logout: (opts) =>
        logout({
          logoutParams: {
            returnTo: opts?.returnTo,
          },
        }),
    }),
    [isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useIsOIDCCallback = (isAuthenticated: boolean) => {
  const [isCallback] = useState(
    () =>
      new URLSearchParams(window.location.search).has("code") &&
      new URLSearchParams(window.location.search).has("state")
  );

  return isCallback && !isAuthenticated;
};

const IdentityAdapter = ({ children }: { children: ReactNode }) => {
  const oidc = useAuth();
  const isCallback = useIsOIDCCallback(oidc.isAuthenticated);

  const value: AuthContextValue = useMemo(
    () => ({
      isLoading: oidc.isLoading || isCallback,
      isAuthenticated: oidc.isAuthenticated,
      provider: "hivemind" as const,
      availableProviders: ["hivemind"] as AuthProviderType[],
      getAccessToken: async () => {
        if (oidc.user?.access_token) return oidc.user.access_token;
        throw new Error("No access token available");
      },
      login: () => oidc.signinRedirect(),
      logout: (opts) =>
        oidc.signoutRedirect({
          post_logout_redirect_uri: opts?.returnTo ?? window.location.origin,
        }),
    }),
    [oidc, isCallback]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const DualIdentityAdapter = ({
  children,
  onSwitchFlow,
}: {
  children: ReactNode;
  onSwitchFlow: (flow: "auth0" | "hivemind") => void;
}) => {
  const oidc = useAuth();
  const isCallback = useIsOIDCCallback(oidc.isAuthenticated);
  const autoRedirected = useRef(false);

  useEffect(() => {
    if (isCallback) {
      autoRedirected.current = true;
      return;
    }

    if (!oidc.isLoading && !oidc.isAuthenticated && !oidc.activeNavigator && !autoRedirected.current) {
      autoRedirected.current = true;
      oidc.signinRedirect();
    }
  }, [isCallback, oidc, oidc.isLoading, oidc.isAuthenticated, oidc.activeNavigator]);

  const value: AuthContextValue = useMemo(
    () => ({
      isLoading: oidc.isLoading || isCallback,
      isAuthenticated: oidc.isAuthenticated,
      provider: "hivemind" as const,
      availableProviders: ["auth0", "hivemind"] as AuthProviderType[],
      getAccessToken: async () => {
        if (oidc.user?.access_token) return oidc.user.access_token;
        throw new Error("No access token available");
      },
      login: (opts?: { connection?: string; provider?: AuthProviderType }) => {
        if (opts?.provider !== "hivemind") {
          sessionStorage.setItem(AUTH_FLOW_KEY, "auth0");
          onSwitchFlow("auth0");
          return Promise.resolve();
        }
        return oidc.signinRedirect();
      },
      logout: (opts) => {
        sessionStorage.removeItem(AUTH_FLOW_KEY);
        return oidc.signoutRedirect({
          post_logout_redirect_uri: opts?.returnTo ?? window.location.origin,
        });
      },
    }),
    [oidc, isCallback, onSwitchFlow]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const DualAuth0Adapter = ({
  children,
  onSwitchFlow,
}: {
  children: ReactNode;
  onSwitchFlow: (flow: "auth0" | "hivemind") => void;
}) => {
  const { getAccessTokenSilently, loginWithRedirect, logout, isLoading, isAuthenticated } = useAuth0();

  const value: AuthContextValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      provider: "auth0" as const,
      availableProviders: ["auth0", "hivemind"] as AuthProviderType[],
      getAccessToken: () => getAccessTokenSilently(),
      login: (opts?: { connection?: string; provider?: AuthProviderType }) => {
        if (opts?.provider === "hivemind") {
          sessionStorage.setItem(AUTH_FLOW_KEY, "hivemind");
          onSwitchFlow("hivemind");
          return Promise.resolve();
        }
        sessionStorage.setItem(AUTH_FLOW_KEY, "auth0");
        return loginWithRedirect({
          authorizationParams: { connection: opts?.connection },
        });
      },
      logout: (opts) => {
        sessionStorage.removeItem(AUTH_FLOW_KEY);
        return logout({
          logoutParams: { returnTo: opts?.returnTo },
        });
      },
    }),
    [isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout, onSwitchFlow]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider = (props: { children?: ReactNode }) => {
  const env = useMemo(() => getEnvConfig(), []);
  const navigate = useNavigate();

  const [activeFlow, setActiveFlow] = useState<"auth0" | "hivemind">(() => {
    const oidcStorageKey = `oidc.user:${env.HIVEMIND_IDENTITY_ISSUER}:${env.HIVEMIND_IDENTITY_CLIENT_ID}`;
    const hasIdentitySession = !!window.localStorage.getItem(oidcStorageKey);
    const authFlow = sessionStorage.getItem(AUTH_FLOW_KEY);
    if (hasIdentitySession || authFlow === "hivemind") return "hivemind";
    return "auth0";
  });

  const handleAuth0Redirect = useCallback(
    (appState) => {
      navigate((appState && appState.returnTo) || window.location.pathname);
    },
    [navigate]
  );

  const handleOIDCSigninCallback = useCallback(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const oidcConfig = {
    authority: env.HIVEMIND_IDENTITY_ISSUER,
    client_id: env.HIVEMIND_IDENTITY_CLIENT_ID,
    redirect_uri: window.location.origin,
    post_logout_redirect_uri: window.location.origin,
    scope: "openid profile email offline_access",
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    onSigninCallback: handleOIDCSigninCallback,
    ...(env.HIVEMIND_IDENTITY_ISSUER ? { extraQueryParams: { audience: env.HIVEMIND_IDENTITY_ISSUER } } : {}),
  };

  const auth0Tree = (adapter: ReactNode) => (
    <Auth0Provider
      domain={env.AUTH0_DOMAIN}
      clientId={env.AUTH0_CLIENT_ID}
      authorizationParams={{
        audience: env.AUTH0_AUDIENCE,
        redirect_uri: window.location.origin,
      }}
      onRedirectCallback={handleAuth0Redirect}
    >
      {adapter}
    </Auth0Provider>
  );

  const oidcTree = (adapter: ReactNode) => <OIDCAuthProvider {...oidcConfig}>{adapter}</OIDCAuthProvider>;

  if (env.AUTH_PROVIDER === "dual") {
    if (activeFlow === "hivemind") {
      return oidcTree(<DualIdentityAdapter onSwitchFlow={setActiveFlow}>{props.children}</DualIdentityAdapter>);
    }

    return auth0Tree(<DualAuth0Adapter onSwitchFlow={setActiveFlow}>{props.children}</DualAuth0Adapter>);
  }

  if (env.AUTH_PROVIDER === "hivemind") {
    return oidcTree(<IdentityAdapter>{props.children}</IdentityAdapter>);
  }

  return auth0Tree(<Auth0Adapter>{props.children}</Auth0Adapter>);
};
